import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography,
  Tabs,
  Alert,
  Button,
  Space,
  Badge
} from 'antd';
import { 
  CrownOutlined,
  DollarOutlined,
  UsergroupDeleteOutlined,
  SettingOutlined,
  EyeOutlined,
  ReloadOutlined,
  BarChartOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import SubscriptionPlans from '../components/SubscriptionPlans';
import SubscriptionConfig from '../components/SubscriptionConfig';
import ActiveSubscriptions from '../components/ActiveSubscriptions';
import SubscriptionTierLimitations from '../components/SubscriptionTierLimitations';

const { Title, Text } = Typography;

const Subscriptions: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const {
    data: subscriptionStats,
    isLoading: loadingStats,
    error: statsError,
    refetch: refetchStats
  } = useQuery({
    queryKey: ['subscription-stats'],
    queryFn: () => adminApi.getSubscriptionStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: subscriptionPlans,
    isLoading: loadingPlans,
    error: plansError
  } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => adminApi.getSubscriptionPlans(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      'free': '#8c8c8c',
      'bronze': '#CD7F32',
      'silver': '#C0C0C0', 
      'gold': '#FFD700',
      'firooze': '#40E0D0'
    };
    return colors[tier] || '#1890ff';
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const revenueGrowth = subscriptionStats 
    ? ((subscriptionStats.revenue_this_month - subscriptionStats.revenue_last_month) / subscriptionStats.revenue_last_month * 100)
    : 0;

  if (statsError || plansError) {
    return (
      <div className="page-content">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Title level={2}>Subscriptions Management</Title>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Alert
              message="Error Loading Data"
              description="Failed to load subscription data. Please check your connection and try again."
              type="error"
              action={
                <Button size="small" danger onClick={() => {
                  refetchStats();
                }}>
                  <ReloadOutlined /> Retry
                </Button>
              }
            />
          </Col>
        </Row>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <EyeOutlined />
          Overview
        </span>
      ),
      children: (
        <div>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card loading={loadingStats}>
                <Statistic
                  title="Total Subscribers"
                  value={subscriptionStats?.total_subscribers || 0}
                  prefix={<CrownOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card loading={loadingStats}>
                <Statistic
                  title="Revenue This Month"
                  value={subscriptionStats?.revenue_this_month || 0}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                  formatter={(value) => formatCurrency(Number(value))}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card loading={loadingStats}>
                <Statistic
                  title="Monthly Subscribers"
                  value={subscriptionStats?.monthly_subscribers || 0}
                  suffix="/ month"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card loading={loadingStats}>
                <Statistic
                  title="Churn Rate"
                  value={subscriptionStats?.churn_rate || 0}
                  prefix={<UsergroupDeleteOutlined />}
                  suffix="%"
                  valueStyle={{ color: (subscriptionStats?.churn_rate || 0) > 10 ? '#cf1322' : '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Revenue Trend */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} lg={12}>
              <Card 
                title="Revenue Comparison" 
                loading={loadingStats}
                extra={
                  <Badge 
                    count={revenueGrowth > 0 ? `+${revenueGrowth.toFixed(1)}%` : `${revenueGrowth.toFixed(1)}%`}
                    style={{ 
                      backgroundColor: revenueGrowth > 0 ? '#52c41a' : '#ff4d4f' 
                    }}
                  />
                }
              >
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="This Month"
                      value={subscriptionStats?.revenue_this_month || 0}
                      formatter={(value) => formatCurrency(Number(value))}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Last Month"
                      value={subscriptionStats?.revenue_last_month || 0}
                      formatter={(value) => formatCurrency(Number(value))}
                      valueStyle={{ color: '#666' }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col xs={24} lg={12}>
              <Card title="Subscription Types" loading={loadingStats}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Monthly Plans"
                      value={subscriptionStats?.monthly_subscribers || 0}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Annual Plans"
                      value={subscriptionStats?.annual_subscribers || 0}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Tier Distribution */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <Card title="Subscription Tier Distribution" loading={loadingStats}>
                <Row gutter={[16, 16]}>
                  {Object.entries(subscriptionStats?.tier_distribution || {}).map(([tier, count]) => (
                    <Col xs={12} sm={8} lg={4} key={tier}>
                      <Statistic
                        title={tier.charAt(0).toUpperCase() + tier.slice(1)}
                        value={count}
                        valueStyle={{ color: getTierColor(tier) }}
                      />
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Recent Activity */}
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card 
                title="Recent Subscriptions" 
                loading={loadingStats}
                extra={
                  <Badge 
                    count={subscriptionStats?.recent_subscriptions?.length || 0}
                    style={{ backgroundColor: '#52c41a' }}
                  />
                }
              >
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {subscriptionStats?.recent_subscriptions?.map((sub, index) => (
                    <div key={index} style={{ 
                      padding: '8px 0', 
                      borderBottom: index < subscriptionStats.recent_subscriptions.length - 1 ? '1px solid #f0f0f0' : 'none' 
                    }}>
                      <div style={{ fontWeight: 500 }}>{sub.username || sub.user_id}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {sub.tier.charAt(0).toUpperCase() + sub.tier.slice(1)} • {sub.period} • {formatCurrency(sub.amount || 0)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        {new Date(sub.started_at).toLocaleDateString()}
                      </div>
                    </div>
                  )) || <div style={{ textAlign: 'center', color: '#999' }}>No recent subscriptions</div>}
                </div>
              </Card>
            </Col>
            
            <Col xs={24} lg={12}>
              <Card 
                title="Recent Cancellations" 
                loading={loadingStats}
                extra={
                  <Badge 
                    count={subscriptionStats?.recent_cancellations?.length || 0}
                    style={{ backgroundColor: '#ff4d4f' }}
                  />
                }
              >
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {subscriptionStats?.recent_cancellations?.map((sub, index) => (
                    <div key={index} style={{ 
                      padding: '8px 0', 
                      borderBottom: index < subscriptionStats.recent_cancellations.length - 1 ? '1px solid #f0f0f0' : 'none' 
                    }}>
                      <div style={{ fontWeight: 500 }}>{sub.username || sub.user_id}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {sub.tier.charAt(0).toUpperCase() + sub.tier.slice(1)} • {sub.period}
                      </div>
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        Expires: {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : 'Unknown'}
                      </div>
                    </div>
                  )) || <div style={{ textAlign: 'center', color: '#999' }}>No recent cancellations</div>}
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      )
    },
    {
      key: 'plans',
      label: (
        <span>
          <CrownOutlined />
          Plans & Pricing
        </span>
      ),
      children: <SubscriptionPlans plans={subscriptionPlans} loading={loadingPlans} />
    },
    {
      key: 'active',
      label: (
        <span>
          <CrownOutlined />
          Active Subscriptions
        </span>
      ),
      children: <ActiveSubscriptions />
    },
    {
      key: 'limitations',
      label: (
        <span>
          <LockOutlined />
          Tier Limitations
        </span>
      ),
      children: <SubscriptionTierLimitations />
    },
    {
      key: 'config',
      label: (
        <span>
          <SettingOutlined />
          Configuration
        </span>
      ),
      children: <SubscriptionConfig />
    },
    {
      key: 'analytics',
      label: (
        <span>
          <BarChartOutlined />
          Analytics
        </span>
      ),
      children: (
        <div>
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col span={24}>
              <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                <div>
                  <Title level={3} style={{ margin: 0 }}>Advanced Analytics</Title>
                  <Text type="secondary">Comprehensive insights and performance metrics</Text>
                </div>
              </Space>
            </Col>
          </Row>
          
          {/* This would be populated with comprehensive analytics data */}
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Alert
                  message="Analytics Dashboard"
                  description="Comprehensive analytics dashboard showing user behavior, revenue trends, subscription performance, and detailed usage metrics will be displayed here when connected to the new analytics API."
                  type="info"
                  showIcon
                />
              </Card>
            </Col>
          </Row>
        </div>
      )
    }
  ];

  return (
    <div className="page-content">
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Title level={2} style={{ margin: 0 }}>Subscriptions Management</Title>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => refetchStats()}
              loading={loadingStats}
            >
              Refresh Data
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs 
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        items={tabItems}
      />
    </div>
  );
};

export default Subscriptions;